import { useCallback } from 'react';
import { useChatStore } from '@store/chatStore';
import { useFeedbackApi } from './useFeedbackApi';

interface UseReferenceFeedbackParams {
    sessionId: string;
    effectiveSessionId: string;
}

/**
 * 引用文献反馈处理 Hook
 * 负责 ThinkProcessV2 中 references 的点赞/点踩/取消操作
 */
export const useReferenceFeedback = ({ sessionId, effectiveSessionId }: UseReferenceFeedbackParams) => {
    const updateThinkProcessV2ReferenceStatus = useChatStore((state) => state.updateThinkProcessV2ReferenceStatus);
    const { callFeedbackApi } = useFeedbackApi({ sessionId });

    /**
     * 处理引用文献点赞/点踩
     * @param messageId - 消息 ID
     * @param segmentIdx - segment 索引（thinkProcessV2 的 segment_idx）
     * @param refId - 引用文献反馈 fragmentId
     * @param type - 操作类型
     * @param tags - 反馈标签（可选）
     * @param content - 反馈内容（可选）
     */
    const handleReferenceFeedback = useCallback(
        async (
            messageId: string,
            segmentIdx: string,
            refId: string,
            type: 'like' | 'unlike',
            tags?: string[],
            content?: string
        ): Promise<{ success: boolean }> => {
            // 参数验证
            if (!messageId || !segmentIdx || !refId) {
                console.error('Missing required parameters in handleReferenceFeedback', {
                    messageId,
                    segmentIdx,
                    refId,
                });
                return { success: false };
            }

            const result = await callFeedbackApi(type, {
                messageId,
                fragmentId: refId,
                tags,
                content,
            });

            if (result.success) {
                // 更新 Store 中的 thinkProcessV2 引用文献状态
                updateThinkProcessV2ReferenceStatus(
                    effectiveSessionId,
                    messageId,
                    segmentIdx,
                    refId,
                    type,
                    1 // status: 1 表示选中
                );
            }

            return result;
        },
        [effectiveSessionId, updateThinkProcessV2ReferenceStatus, callFeedbackApi]
    );

    /**
     * 处理取消引用文献反馈
     */
    const handleReferenceCancel = useCallback(
        async (messageId: string, segmentIdx: string, refId: string): Promise<{ success: boolean }> => {
            // 参数验证
            if (!messageId || !segmentIdx || !refId) {
                console.error('Missing required parameters in handleReferenceCancel', {
                    messageId,
                    segmentIdx,
                    refId,
                });
                return { success: false };
            }

            const result = await callFeedbackApi('cancel', {
                messageId,
                fragmentId: refId,
            });

            if (result.success) {
                // 更新 Store：清除 like 状态
                updateThinkProcessV2ReferenceStatus(
                    effectiveSessionId,
                    messageId,
                    segmentIdx,
                    refId,
                    'like',
                    0 // status: 0 表示未选中
                );
                // 清除 unlike 状态
                updateThinkProcessV2ReferenceStatus(effectiveSessionId, messageId, segmentIdx, refId, 'unlike', 0);
            }

            return result;
        },
        [effectiveSessionId, updateThinkProcessV2ReferenceStatus, callFeedbackApi]
    );

    return {
        handleReferenceFeedback,
        handleReferenceCancel,
    };
};
